/**
 * Maps CascadeFallStyle values to GSAP easing equivalents.
 * Replaces the CSS cubic-bezier() and @keyframes from globals.css.
 */
import type { CascadeFallStyle } from '@core/types/cascade-slot.types';

/** Simple easing map: CascadeFallStyle → GSAP ease string */
export const GSAP_EASING_MAP: Record<CascadeFallStyle, string> = {
  bounce: 'back.out(1.4)',       // CSS: cubic-bezier(0.34, 1.56, 0.64, 1)
  smooth: 'power2.out',          // CSS: ease (0.25, 0.1, 0.25, 1)
  elastic: 'elastic.out(1, 0.3)', // CSS: cubic-bezier(0.68, -0.55, 0.27, 1.55)
  drop: 'power4.out',            // CSS: cubic-bezier(0.55, 0, 1, 0.45)
  fade: 'power1.out',            // CSS: ease-out
};

/**
 * Build GSAP keyframes for gravity fall animation (existing symbols falling down).
 * Replicates cascadeGravity{Style} keyframes from globals.css lines 299-362.
 *
 * @param startY - Starting Y position (above target)
 * @param targetY - Final resting Y position
 * @param cellHeight - Height of one cell (for bounce/overshoot amounts)
 * @param duration - Total animation duration in seconds
 * @param style - The cascade fall style
 * @param delay - Column-based stagger delay in seconds
 */
export function buildGravityKeyframes(
  startY: number,
  targetY: number,
  cellHeight: number,
  duration: number,
  style: CascadeFallStyle,
  delay: number
): gsap.TweenVars {
  const base = { delay, overwrite: 'auto' as const };

  switch (style) {
    case 'bounce':
      // Overshoot then settle: 0%→target+4%→target-2%→target
      return {
        ...base,
        keyframes: [
          { y: targetY + cellHeight * 0.04, duration: duration * 0.7, ease: 'power2.in' },
          { y: targetY - cellHeight * 0.02, duration: duration * 0.15, ease: 'power1.out' },
          { y: targetY, duration: duration * 0.15, ease: 'power1.inOut' },
        ],
      };

    case 'smooth':
      // Smooth ease to target
      return {
        ...base,
        y: targetY,
        duration,
        ease: 'power2.out',
      };

    case 'elastic':
      // Spring overshoot: 0%→target+6%→target-3%→target+1%→target
      return {
        ...base,
        keyframes: [
          { y: targetY + cellHeight * 0.06, duration: duration * 0.5, ease: 'power2.in' },
          { y: targetY - cellHeight * 0.03, duration: duration * 0.2, ease: 'power1.out' },
          { y: targetY + cellHeight * 0.01, duration: duration * 0.15, ease: 'power1.inOut' },
          { y: targetY, duration: duration * 0.15, ease: 'power1.out' },
        ],
      };

    case 'drop':
      // Fast accelerating drop with slight overshoot
      return {
        ...base,
        keyframes: [
          { y: targetY + cellHeight * 0.02, duration: duration * 0.8, ease: 'power4.in' },
          { y: targetY, duration: duration * 0.2, ease: 'power2.out' },
        ],
      };

    case 'fade':
      // Simple ease-out drop
      return {
        ...base,
        y: targetY,
        duration,
        ease: 'power1.out',
      };
  }
}

/**
 * Build GSAP keyframes for fill animation (new symbols entering from top).
 * Replicates cascadeFall{Style} keyframes from globals.css.
 *
 * @param entryY - Y position above viewport where symbol starts (offscreen)
 * @param targetY - Final resting Y position
 * @param cellHeight - Height of one cell
 * @param duration - Total animation duration in seconds
 * @param style - The cascade fall style
 * @param delay - Column-based stagger delay in seconds
 */
export function buildFillKeyframes(
  entryY: number,
  targetY: number,
  cellHeight: number,
  duration: number,
  style: CascadeFallStyle,
  delay: number
): gsap.TweenVars {
  const base = { delay, overwrite: 'auto' as const };

  switch (style) {
    case 'bounce':
      return {
        ...base,
        keyframes: [
          { y: targetY + cellHeight * 0.04, duration: duration * 0.7, ease: 'power2.in' },
          { y: targetY - cellHeight * 0.02, duration: duration * 0.15, ease: 'power1.out' },
          { y: targetY, duration: duration * 0.15, ease: 'power1.inOut' },
        ],
      };

    case 'smooth':
      return {
        ...base,
        y: targetY,
        duration,
        ease: 'power2.out',
      };

    case 'elastic':
      return {
        ...base,
        keyframes: [
          { y: targetY + cellHeight * 0.06, duration: duration * 0.5, ease: 'power2.in' },
          { y: targetY - cellHeight * 0.03, duration: duration * 0.2, ease: 'power1.out' },
          { y: targetY + cellHeight * 0.01, duration: duration * 0.15, ease: 'power1.inOut' },
          { y: targetY, duration: duration * 0.15, ease: 'power1.out' },
        ],
      };

    case 'drop':
      return {
        ...base,
        keyframes: [
          { y: targetY + cellHeight * 0.02, duration: duration * 0.8, ease: 'power4.in' },
          { y: targetY, duration: duration * 0.2, ease: 'power2.out' },
        ],
      };

    case 'fade':
      return {
        ...base,
        y: targetY,
        duration,
        ease: 'power1.out',
      };
  }
}

/**
 * Build GSAP tween vars for the cascadeRemove animation.
 * Replicates @keyframes cascadeRemove from globals.css.
 */
export function buildRemoveTween(
  currentY: number,
  duration: number,
  delay: number
): gsap.TweenVars {
  return {
    alpha: 0,
    y: currentY + 15,
    duration,
    ease: 'power2.in',
    delay,
  };
}

export function buildRemoveScaleTween(
  duration: number,
  delay: number
): gsap.TweenVars {
  return {
    x: 0.3,
    y: 0.3,
    duration,
    ease: 'power2.in',
    delay,
  };
}

/**
 * Build GSAP tween vars for winPulse animation.
 * Replicates @keyframes winPulse from globals.css.
 */
export function buildWinPulseTween(
  winScale: number
): gsap.TweenVars {
  return {
    x: winScale,
    y: winScale,
    duration: 0.3,
    yoyo: true,
    repeat: -1,
    ease: 'sine.inOut',
  };
}
